import React from 'react';
import { Header } from 'semantic-ui-react';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';

// Fixed-order categorical palette (validated for CVD-safe adjacent contrast).
// Never cycle or reassign by rank — see the dataviz skill's color-formula.
const CATEGORICAL_COLORS = [
  '#2a78d6', // blue
  '#eb6834', // orange
  '#1baf7a', // aqua
  '#eda100', // yellow
  '#e87ba4', // magenta
  '#008300', // green
  '#4a3aa7', // violet
  '#e34948', // red
];
const SINGLE_SERIES_COLOR = CATEGORICAL_COLORS[0];
const GRIDLINE_COLOR = '#e1e0d9';
const AXIS_COLOR = '#898781';
const CHART_MARGIN = {
  top: 8,
  right: 16,
  bottom: 8,
  left: 0,
};

const formatValue = (value, unit) => (unit ? `${value}${unit}` : value);

const PieChartBlock = ({ data, unit }) => (
  <ResponsiveContainer width="100%" height={280}>
    <PieChart>
      <Pie data={data} dataKey="value" nameKey="label" outerRadius={100} label={(entry) => entry.label}>
        {data.map((entry, index) => (
          <Cell key={entry.label} fill={CATEGORICAL_COLORS[index % CATEGORICAL_COLORS.length]} />
        ))}
      </Pie>
      <Tooltip formatter={(value) => formatValue(value, unit)} />
      <Legend />
    </PieChart>
  </ResponsiveContainer>
);

const BarChartBlock = ({ data, unit }) => (
  <ResponsiveContainer width="100%" height={280}>
    <BarChart data={data} margin={CHART_MARGIN}>
      <CartesianGrid vertical={false} stroke={GRIDLINE_COLOR} />
      <XAxis dataKey="label" stroke={AXIS_COLOR} tick={{ fontSize: 12 }} />
      <YAxis stroke={AXIS_COLOR} tick={{ fontSize: 12 }} />
      <Tooltip formatter={(value) => formatValue(value, unit)} />
      <Bar dataKey="value" fill={SINGLE_SERIES_COLOR} radius={[4, 4, 0, 0]} />
    </BarChart>
  </ResponsiveContainer>
);

const LineChartBlock = ({ data, unit }) => (
  <ResponsiveContainer width="100%" height={280}>
    <LineChart data={data} margin={CHART_MARGIN}>
      <CartesianGrid vertical={false} stroke={GRIDLINE_COLOR} />
      <XAxis dataKey="label" stroke={AXIS_COLOR} tick={{ fontSize: 12 }} />
      <YAxis stroke={AXIS_COLOR} tick={{ fontSize: 12 }} />
      <Tooltip formatter={(value) => formatValue(value, unit)} />
      <Line type="monotone" dataKey="value" stroke={SINGLE_SERIES_COLOR} strokeWidth={2} dot={{ r: 4 }} />
    </LineChart>
  </ResponsiveContainer>
);

const Chart = ({ block }) => {
  const {
    chartType, title, data, unit,
  } = block;

  return (
    <div style={{ marginTop: '1.5em', marginBottom: '1em' }}>
      <Header as="h5">{title}</Header>
      {chartType === 'pie' && <PieChartBlock data={data} unit={unit} />}
      {(chartType === 'bar' || chartType === 'histogram') && <BarChartBlock data={data} unit={unit} />}
      {chartType === 'line' && <LineChartBlock data={data} unit={unit} />}
    </div>
  );
};

export default Chart;
